
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError, tap, switchMap, Subject, mergeMap, combineLatest, defer } from 'rxjs';
import { map, filter,  catchError, finalize, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';
import * as _ from 'underscore';

import { UserModel } from '../../../Core/Users/models/user.model';
import { AccountModel } from '../../../Core/Users/models/account.model';
import { UsersApi } from '../../../api/users/users.api';
import { UserPermissionApiModel } from '../../../api/users/models/user.permission.api.model';
import { UserPermissionModel } from '../models/user.permission.model';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { QuotaInformation } from '../../../api/mediaSubscriptions/models/quota-information.api.model';
import { MediaSubscriptionApiService } from '../../../api/mediaSubscriptions/media-subscription.api.service';
import { MonitoringAccountConfigurationApi } from '../../../api/monitoring/monitoring-account-configuration.api';
import { LicensesEnum } from '../../../api/monitoring/licenses.enum';
import { SublicensesEnum } from '../../../api/monitoring/sublicenses.enum';
import { AccountApiModel } from '../../../api/accounts/models/account.api.model';
import { TargetingPreferencesApi } from '../../../api/targeting/targeting-preferences.api';
import { TargetingAccountPreferencesApiModel } from '../../../api/targeting/model/targeting-account-preferences.api.model';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { MonitoringConfigsModel, MonitoringConfigTVEyes, SubscriptionLicenses } from '../models/monitoring.configs.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { UserStateModel } from '../../../Core/Users/models/user-state.model';
import { AIAgentQuotaAdminApiService } from '../../../api/agents/AI-agent-quota-admin.api.service';
import { AutomationAgentQuotaModel } from '../models/automation-agent-quota.model';
import { WireQuotaApiModel } from '../../../api/wire/models/wire-quota-api.model';
import { WireApi } from '../../../api/wire/wire.api';
import { LeftSidebarService } from '../../../Common/Layout/services/left-sidebar.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TargetingPreferencesService } from '../../../Common/TargetingShared/services/targeting-preferences.service';
import { TargetingQuotaInfo } from '../../../Common/TargetingShared/model/targeting.quota.info';
import { UserProfileDialogComponent } from '../components/UserProfile/UserProfileDialog/user-profile-dialog.component';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { CommonApiErrorModel } from '../../../api/genericApi/models/common-api-error.model';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { FilterTypeSingleTextModel } from '../../../Common/Filter/models/filter-type-single-text.model';
import { timezoneConversionDateFormat } from '../../../Common/Forms/helpers/time.helper';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { AccountSummaryModel } from '@Core/Users/models/account-summary.model';
import { BriefingConfig } from '../models/briefing-config.model';
import { BriefingConfigApiService } from '@api/monitoring/briefing-config.api.service';
import { BriefingConfigService } from '@Common/MonitorShared/services/briefing-config.service';
import { UpdatePreferencesApiModel } from '../../../api/users/models/preferences.api.model';
import { EmailAccountConnectorConfigApi } from '../../../api/emailAccountConnector/models/email-account-connector-config.api';
import { EmailAccountConnectorApiService } from '../../../api/emailAccountConnector/email-account-connector.api.service';
import { MediaServiceApi } from '../../../api/mediaService/media-service.api';
import { EmailAccountConnectorUsage } from '../models/email-account-connector-usage.model';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { ConfigService } from '@Core/Config/services/config.service';
import { StorageSettingsModel } from '../../../Common/ContentLibrary/models/storage-settings.model';
import { AccountNoteApi } from '../../../api/accountNote/accountNote.api';
import { AccountNoteModel } from '../models/account-note.model';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { ApiKeyService } from './api-key.service';
import { ApiKey } from '../models/api-key';
import { GeoRestriction } from '../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';
import { AgentsApiService } from '../../../api/agents/agents.api';
import { AgentConfigurationApi } from '../../../api/agents/models/agent-configuration.api.model';

interface AmtLeftSidebarModeConfig {
    mode: Mode;
    isComplete: boolean;
    listItemSource: LeftSidebarListItem[];
    localizations: LeftSidebarLocalizations;
    // getButtons: (item: any) => LeftSidebarListItemButton[];
    isAmplifyButtonEnabled: boolean;
    amplifyAction?: () => void;

    // amplifyDialogConfig: AmplifyDialogModel

    filters?: FilterTypeModel[];
    prepareFilters?: () => void; // set name, get checkbox options, etc.
    applyFilters?: (items: LeftSidebarListItemConvertableModel[]) => LeftSidebarListItemConvertableModel[];
}

@Injectable()
export class AccountManagementService extends LeftSidebarService implements OnDestroy {
    protected readonly nameFilter = new FilterTypeSingleTextModel(
        'name',
        'name',
        [],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true
    );

    private subscriptions: Subscription[] = [];

    private selectedAccount: AccountModel;
    private originalAccount: AccountModel;
    private primaryAccount: AccountSummaryModel;
    private selectedUserAccounts: AccountModel[];
    private selectedAccountUsers: UserModel[];
    // Holds results until user has selected an account
    private selectedUserPermissions: UserPermissionApiModel[];
    private permissions: UserPermissionModel[];

    private selectedUser: UserModel;
    private originalUser: UserModel;

    public selectedUser$: BehaviorSubject<UserModel>;

    private selectedAccountProvider: BehaviorSubject<AccountModel>;
    private selectedUserAccountsProvider: BehaviorSubject<AccountModel[]>;
    private selectedAccountUsersProvider: BehaviorSubject<UserModel[]>;
    private permissionsProvider: BehaviorSubject<UserPermissionModel[]>;
    private primaryAccount$: BehaviorSubject<AccountSummaryModel>;

    private selectedAccountNotesSubject: BehaviorSubject<AccountNoteModel[]> = new BehaviorSubject<AccountNoteModel[]>([]);
    public selectedAccountNotes$ = this.selectedAccountNotesSubject.asObservable();

    private selectedAccountApiKeysSubject: BehaviorSubject<ApiKey[]> = new BehaviorSubject<ApiKey[]>([]);
    public selectedAccountApiKeys$ = this.selectedAccountApiKeysSubject.asObservable();

    private mediaSubscriptionQuotaGroup: QuotaInformation;
    private mediaSubscriptionQuotaGroup$ = new  BehaviorSubject<QuotaInformation>(null);
    private originalMediaSubscriptionQuotaGroup: QuotaInformation;

    private socialListeningQuotaGroup: QuotaInformation;
    private socialListeningQuotaGroup$ = new  BehaviorSubject<QuotaInformation>(null);
    private originalSocialListeningQuotaGroup: QuotaInformation;

    private monitoringConfigs: MonitoringConfigsModel;
    private monitoringConfigs$ = new BehaviorSubject<MonitoringConfigsModel>(null);
    private originalMonitoringConfigs: MonitoringConfigsModel;

    private briefingConfigs: BriefingConfig;
    private briefingConfigs$ = new BehaviorSubject<BriefingConfig>(null);
    private originalBriefingConfigs: BriefingConfig;

    private agentConfiguration: AgentConfigurationApi | null = null;
    private agentConfiguration$ = new BehaviorSubject<AgentConfigurationApi | null>(this.agentConfiguration);
    private originalAgentConfiguration: AgentConfigurationApi | null = null;

    private hasOpenAiPermissionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasOpenAiPermission$ = this.hasOpenAiPermissionSubject.asObservable();

    private hasAgenticFeaturePermissionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasAgenticFeaturePermission$ = this.hasAgenticFeaturePermissionSubject.asObservable();

    private hasMediaMonitoringApisSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasMediaMonitoringApis$ = this.hasMediaMonitoringApisSubject.asObservable();

    private hasApiKeyManagersSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasApiKeyManagers$ = this.hasApiKeyManagersSubject.asObservable();

    private licensesChangedSubject$ = new Subject<void>();
    public licensesChanged$ = this.licensesChangedSubject$.asObservable();

    private targetingAccountPreferences: TargetingAccountPreferencesApiModel;
    private originalTargetingAccountPreferences: TargetingAccountPreferencesApiModel;
    private targetingAccountPreferences$: BehaviorSubject<TargetingAccountPreferencesApiModel> = new BehaviorSubject<TargetingAccountPreferencesApiModel>(null);

    private emailAccountConnectorConfig: EmailAccountConnectorConfigApi;
    private originalEmailAccountConnectorConfig: EmailAccountConnectorConfigApi;
    // eslint-disable-next-line max-len
    private emailAccountConnectorConfigSubject = new BehaviorSubject<EmailAccountConnectorConfigApi>(null);
    public emailAccountConnectorConfig$: Observable<EmailAccountConnectorConfigApi> = this.emailAccountConnectorConfigSubject.asObservable();
    private emailAccountConnectorUsageSubject = new BehaviorSubject<EmailAccountConnectorUsage>(null);
    public emailAccountConnectorUsage$: Observable<EmailAccountConnectorUsage> = this.emailAccountConnectorUsageSubject.asObservable();

    // store current value
    private contentLibraryStorageSettings: StorageSettingsModel;
    private originalContentLibraryStorage: number;
    // emits when value changes
    private contentLibraryStorageSettingsSubject: BehaviorSubject<StorageSettingsModel> = new BehaviorSubject<StorageSettingsModel>(null);
    public contentLibraryStorageSettings$: Observable<StorageSettingsModel> = this.contentLibraryStorageSettingsSubject.asObservable();

    private targetingAccountQuotaInfo: TargetingQuotaInfo;
    private originalTargetingAccountQuotaInfo: TargetingQuotaInfo;
    private targetingAccountQuotaInfo$: BehaviorSubject<TargetingQuotaInfo> = new BehaviorSubject<TargetingQuotaInfo>(null);

    private wireQuotas: WireQuotaApiModel[] = [];
    private originalWireQuotas: WireQuotaApiModel[];
    private wireQuotas$: BehaviorSubject<WireQuotaApiModel[]> = new BehaviorSubject<WireQuotaApiModel[]>(this.wireQuotas);

    private automationAgentQuota: AutomationAgentQuotaModel = new AutomationAgentQuotaModel();
    private originalAutomationAgentQuota: AutomationAgentQuotaModel;
    private automationAgentQuota$: BehaviorSubject<AutomationAgentQuotaModel> =
        new BehaviorSubject<AutomationAgentQuotaModel>(this.automationAgentQuota);

    public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private refreshMediaDBAccountSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public refreshMediaDBAccount$ = this.refreshMediaDBAccountSubject.asObservable();

    // LSB stuff
    private dropdownItems: DropdownOptionModel[];
    private dropdownItems$: BehaviorSubject<DropdownOptionModel[]>;
    private selectedDropdownItem: DropdownOptionModel;
    private selectedDropdownItem$: BehaviorSubject<DropdownOptionModel>;
    private listAccounts: LeftSidebarListItem[] = [];
    private listUsers: LeftSidebarListItem[] = [];
    private selectedListItem: LeftSidebarListItem;
    private selectedListItem$: BehaviorSubject<LeftSidebarListItem>;
    private localizations: LeftSidebarLocalizations;
    private localizations$: BehaviorSubject<LeftSidebarLocalizations>;

    // this is extracted from b/e error message, and will be reset once the prominence limit get changed by user
    // prominenceInUse !== 0 means error
    prominenceInUse: number = 0;
    prominenceInUse$ = new BehaviorSubject<number>(this.prominenceInUse);

    userToAdd: UserModel;
    userToAdd$: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(null);

    currentMode: Mode = Mode.account;
    creatingUserFromButton: boolean = false;
    userPrefTimezone: string;

    hasEmailConnectorConfigSetup = false;

    private amtLeftSidebarModeConfigMap: Map<Mode, AmtLeftSidebarModeConfig> = new Map([
        [
            Mode.account,
            {
                mode: Mode.account,
                isComplete: true,
                listItemSource: this.listAccounts,
                isAmplifyButtonEnabled: true,
                // getButtons: this.getWireButtons.bind(this),
                localizations: {
                    amplifyButton: 'AMT.LHP.button',
                    itemsHeading: 'AMT.LHP.heading',
                    statusHeading: 'AMT.LHP.statusHeading',
                    noItems: 'AMT.LHP.noItems'
                },
                amplifyAction: () => {
                    const textInputDialogConfig: TextInputDialogModel = {
                        inputs: {
                            heading: 'AMT.createAccount.dialog.heading',
                            description: 'AMT.createAccount.dialog.description',
                            placeholder: 'AMT.createAccount.dialog.placeholder',
                            okLabel: 'create',
                            cancelLabel: 'cancel',
                            width: 300,
                            submit: (name: string) => {
                                const apiCall = this.usersApi.createAccount(
                                    {
                                        name: name,
                                        neptuneOnly: true,
                                        hasUiAccess: true,
                                        expiryDate : moment().add(1, 'years').format()
                                    }
                                );
                                apiCall.subscribe(id => {
                                    this.messageService.add(
                                        {
                                            severity : messageSeverityEnum.success,
                                            summary : this.localizationService.transform('AMT.createAccount.growlMsg.success', { name: name })
                                        });
                                    this.router.navigate(
                                        ['AccountManagement', 'account', id]
                                    );
                                });
                                return apiCall;
                            },
                            formValidator: (listName: string) => {
                                // TODO : check duplicate name?
                                return [];
                            }
                        },
                        outputs: {}
                    };
                    this.coreDialogService.open(
                        TextInputDialogComponent,
                        textInputDialogConfig
                    );

                },
                filters: [
                    this.nameFilter
                ],
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('AMT.LHP.filterPlaceholder');
                    this.nameFilter.label = 'AMT.LHP.filterLabel';

                    // this.getAuditableFilterOptions(this.listItems);
                },
                applyFilters: (items: any[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    return filteredItems;
                }
            },

        ],
        [
            Mode.user,
            {
                mode: Mode.user,
                isComplete: true,
                listItemSource: this.listUsers,
                isAmplifyButtonEnabled: true,
                // getButtons: this.getWireButtons.bind(this),
                localizations: {
                    amplifyButton: 'UMT.LHP.button',
                    itemsHeading: 'UMT.LHP.heading',
                    statusHeading: 'UMT.LHP.statusHeading',
                    noItems: 'UMT.LHP.noItems'
                },
                amplifyAction: () => {
                    const user = new UserModel();
                    this.creatingUserFromButton = true;
                    this.coreDialogService.open(UserProfileDialogComponent, {
                        inputs: {
                            user: user,
                            addToCurrentAccount: false
                        }
                    });

                    // this.coreDialogService.open(WireCreateDialogComponent);
                },
                filters: [
                    this.nameFilter
                ],
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('UMT.LHP.filterPlaceholder');
                    this.nameFilter.label = 'AMT.LHP.filterLabel';

                    // this.getAuditableFilterOptions(this.listItems);
                },
                applyFilters: (items: any[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    return filteredItems;
                }
            },

        ]
    ]);

    private locale: string = 'en-US';

    private duplicateUsernameErrorCode: string = 'COM-001';

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        private usersApi: UsersApi,
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private monitoringAccountApi: MonitoringAccountConfigurationApi,
        private briefingAccountApi: BriefingConfigApiService,
        private targetingPreferencesApi: TargetingPreferencesApi,
        private wireApi: WireApi,
        private router: Router,
        private coreDialogService: CoreDialogService,
        protected usersService: UsersService,
        private monitorConfigService: MonitorConfigService,
        protected localizationService: LocalizationService,
        private targetingPreferencesService: TargetingPreferencesService,
        protected displayGroupsService: DisplayGroupsService,
        private briefingConfigService: BriefingConfigService,
        private emailAccountConnectorApi: EmailAccountConnectorApiService,
        private configurationService: ConfigService,
        private mediaServiceApi: MediaServiceApi,
        private accountNoteApi: AccountNoteApi,
        private apiKeyService: ApiKeyService,
        private aiAgentQuotaAdminApi: AIAgentQuotaAdminApiService,
        private agentsApiService: AgentsApiService
    ) {
        super(usersService, displayGroupsService, localizationService);

        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x))
                .subscribe(languageTag => {
                    this.locale = languageTag;
                }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(pref => this.userPrefTimezone = pref.timeZone)
        );

        this.dropdownItems = [
            new DropdownOptionModel('AMT.LHP.heading', Mode.account),
            new DropdownOptionModel('UMT.LHP.heading', Mode.user)
        ];
        this.dropdownItems$ = new BehaviorSubject(this.dropdownItems);

        this.selectedDropdownItem = this.dropdownItems[0];
        this.selectedDropdownItem$ = new BehaviorSubject(this.selectedDropdownItem);
        this.listItemsProvider = new BehaviorSubject(this.listItems);
        this.listItemGroupsProvider = new BehaviorSubject(this.listItemGroups);
        this.selectedListItem$ = new BehaviorSubject(null);
        this.primaryAccount$ = new BehaviorSubject(null);

        this.topListItem = null;
        this.topListItemProvider = new BehaviorSubject(this.topListItem);

        this.localizations = this.amtLeftSidebarModeConfigMap.get(this.currentMode).localizations;
        this.localizations$ = new BehaviorSubject(this.localizations);

        this.selectedUser$ = new BehaviorSubject<UserModel>(null);
        this.selectedAccountProvider = new BehaviorSubject<AccountModel>(null);
        this.selectedUserAccountsProvider = new BehaviorSubject<AccountModel[]>([]);
        this.selectedAccountUsersProvider = new BehaviorSubject<UserModel[]>([]);
        this.permissionsProvider = new BehaviorSubject<UserPermissionModel[]>([]);

        this.router.events.subscribe((e) => {
            if (e instanceof NavigationEnd) {
                if(e.urlAfterRedirects.match(/AccountManagement\/user/)){
                    if (this.currentMode !== Mode.user) {
                        this.currentMode = Mode.user;
                        this.applyConfig();
                    }
                    this.selectedDropdownItem$.next(this.dropdownItems[1]);
                    this.primaryHeading = 'UMT.primaryHeading';
                } else if (e.urlAfterRedirects.match(/AccountManagement/)) {
                    if (this.currentMode !== Mode.account) {
                        this.currentMode = Mode.account;
                        this.applyConfig();
                    }
                    this.selectedDropdownItem$.next(this.dropdownItems[0]);
                    this.primaryHeading = 'AMT.primaryHeading';
                }
                const urlParse: RegExp = /AccountManagement\/(account|user)\/(\d+)/;
                const matches = e.urlAfterRedirects.match(urlParse);
                if (matches && matches[1]) {
                    if (matches[1] === 'account') {
                        this.selectAccount(+matches[2]).then(done => {
                            // If we land on an account by URL, the LHP is currently
                            // unpopulated since the user has not yet initiated a search.
                            // In this case, just add the entry for the account we landed on
                            if (this.listItems.length === 0) {
                                this.listItems = [
                                    {
                                        label: this.selectedAccount.name,
                                        value: this.selectedAccount.id,
                                        status: this.selectedAccount.id,
                                        buttons : []
                                    }
                                ];
                                this.listItemsProvider.next(this.listItems);
                            }
                            this.selectedListItem = this.listItems.find(x => x.value === this.selectedAccount.id);
                            this.selectedListItem$.next(this.selectedListItem);
                        });
                    } else if (matches[1] === 'user') {
                        this.selectUser(+matches[2]).then(done => {
                            // If we land on a user by URL, the LHP is currently
                            // unpopulated since the user has not yet initiated a search.
                            // In this case, just add the entry for the account we landed on
                            if (this.listItems.length === 0) {
                                this.listItems = [
                                    {
                                        label: this.selectedUser.firstName + ' ' + this.selectedUser.lastName,
                                        value: this.selectedUser.id,
                                        status: this.selectedUser.id,
                                        buttons : []
                                    }
                                ];
                                this.listItemsProvider.next(this.listItems);
                            }
                            this.selectedListItem = this.listItems.find(x => x.value === this.selectedUser.id);
                            this.selectedListItem$.next(this.selectedListItem);
                        });
                    }
                } else {
                    this.selectedAccount = null;
                    this.selectedAccountProvider.next(null);
                    this.selectedAccountUsers = [];
                    this.selectedAccountUsersProvider.next(this.selectedAccountUsers);
                    this.selectedUser = null;
                    this.selectedUser$.next(null);
                    this.selectedListItem = null;
                    this.selectedListItem$.next(null);
                }
            }
        });

        this.applyConfig();

        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);
        this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate];
        this.lhpSortFields$.next(this.lhpSortFields.slice());

    }

    searchUsers(searchString: string): Observable<UserModel[]> {
        return this.usersApi.searchUsers(searchString).pipe(map(users => users.map(user => new UserModel(user))));
    }

    // TODO : doesnt work cause the backend doesnt return the accountids
    searchUsersNotInAccount(searchString: string): Observable<UserModel[]> {
        return this.searchUsers(searchString).pipe(
            map(users => {
                // we only want to return neptune only users
                return users.filter(x => x.neptuneOnly && x.accountsIds.findIndex(id => id === this.selectedAccount.id) < 0);
            }));
    }

    searchNeptuneOnlyAccounts(searchString: string): Observable<AccountModel[]> {
        return this.usersApi.searchAccounts(searchString).pipe(
            map(accounts => {
                // we only want to return neptune only accounts
                return accounts.map(account => new AccountModel(account)).filter(account => account.neptuneOnly);
            })
        );
    }

    searchMediaDbApiAccounts(searchString: string): Observable<AccountModel[]> {
        return this.usersApi.searchAccounts(searchString).pipe(
            map(accounts => {
                // we only want to return neptune only accounts
                return accounts.map(account => new AccountModel(account)).filter(
                    account => account.neptuneOnly && account.permissions?.includes(userPermissionsEnum.mediaDatabaseApi));
            })
        );
    }

    removeUserFromAccount(user: UserModel, account: AccountModel) {
        // TODO: remove the call to getUser once the backend makes it possible to hide the button first
        this.usersApi.getUser(user.id).pipe(
            tap((apiUser) => {
                if (apiUser.primaryAccountId === account.id) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'AMT.removeUserDialog.growlMsg.error',
                        detail: this.localizationService.transform('AMT.removeUserDialog.growlMsg.error.primaryAccount')
                    });
                } else {
                    const confirmConfig: ConfirmDialogModel = {
                        inputs: {
                            heading:  'AMT.removeUserDialog.heading',
                            message:   this.localizationService.transform(
                                'AMT.removeUserDialog.message',
                                {
                                    first: user.firstName,
                                    last: user.lastName,
                                    account: account.name
                                }),
                            okLabel: 'AMT.removeUserDialog.confirm'
                        },
                        outputs: {
                            // The user passed is a summary object, we need to fetch the full in order to put it
                            // or implement patch or some other backend endpoint
                            dialogClose: (confirmed: boolean) => {
                                if (confirmed) {
                                    this.loading$.next(true);
                                    this.usersApi.getUser(user.id).pipe(
                                        tap(fullUser => fullUser.accountIds = fullUser.accountIds.filter(x => x !== account.id)),
                                        switchMap(fullUser => this.usersApi.updateUser(fullUser).pipe(
                                            catchError((error) => {
                                                const errorMessages: string[] = error?.error?.details?.map(err => err.message) || [];
                                                if (errorMessages?.length) {
                                                    errorMessages.forEach(message => {
                                                        if (message.includes('Primary account selected is not part of user\'s accounts')) {
                                                            this.messageService.add({
                                                                severity: messageSeverityEnum.error,
                                                                summary: 'AMT.removeUserDialog.growlMsg.error',
                                                                detail: this.localizationService.transform('AMT.removeUserDialog.growlMsg.error.primaryAccount')
                                                            });
                                                        }
                                                    });
                                                } else {
                                                    this.messageService.add({
                                                        severity: messageSeverityEnum.error,
                                                        summary: 'AMT.removeUserDialog.growlMsg.error'
                                                    });
                                                }
                                                this.loading$.next(false);
                                                return of('error');
                                            }),
                                            tap((err) => {
                                                if (!err) {
                                                    this.messageService.add(
                                                        {
                                                            severity : messageSeverityEnum.success,
                                                            summary : this.localizationService.transform(
                                                                'AMT.removeUserDialog.growlMsg.success',
                                                                {
                                                                    first: fullUser.firstName,
                                                                    last: fullUser.lastName,
                                                                    account: this.selectedAccount.name
                                                                }
                                                            )
                                                        }
                                                    );
                                                    this.fetchUsersForAccount(account.id);
                                                }
                                            })
                                        ))
                                    ).subscribe();
                                }
                            }
                        }
                    };
                    this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
                }
            })
        ).subscribe();
    }

    selectUser(userId: number) {
        return new Promise<void>((resolve) => {
            this.usersApi.getUser(userId).subscribe(user => {
                this.selectedUser = new UserModel(user);
                this.originalUser = new UserModel(user);
                this.refreshMediaDBAccountSubject.next(false);
                this.selectedUser$.next(this.selectedUser);

                resolve();
            });
        });
    }

    setUserToAdd(userId: number) {
        this.usersApi.getUser(userId).subscribe(user => {
            this.userToAdd = new UserModel(user);
            this.userToAdd$.next(this.userToAdd);
        });
    }

    addUserToSelectedAccount() {
        this.userToAdd.accountsIds.push(this.selectedAccount.id);
        this.usersApi.updateUser(this.userToAdd.toApiModel()).pipe(mergeMap(res => {
            // generating apiKey for users that have media db api accounts
            if (this.selectedAccount.permissions.includes(userPermissionsEnum.mediaDatabaseApi)) {
                return this.usersApi.generateApiKey(this.selectedAccount.id, this.userToAdd.id).pipe(catchError(err => of(null)));
            }

            return of(null);
        })).subscribe(x => {
            this.messageService.add(
                {
                    severity : messageSeverityEnum.success,
                    summary : this.localizationService.transform(
                        'AMT.addToAccount.growlMsg.success',
                        {
                            first: this.userToAdd.firstName,
                            last: this.userToAdd.lastName,
                            account: this.selectedAccount.name
                        })
                });
            this.fetchUsersForAccount(this.selectedAccount.id);
            this.userToAdd = null;
            this.userToAdd$.next(this.userToAdd);
        });
    }

    createUser(user: UserModel, addToCurrentAccount: boolean = true): Promise<number> {

        return new Promise<number>((resolve, reject) => {
            this.usersApi.createUser(user.toApiModel()).pipe(
                mergeMap(result => {
                    const newUserId = result.userId;
                    // generating apiKey for users that have media db api accounts
                    const generateMediaApiAccountCalls: Observable<string>[] = [];

                    user.mediaDatabaseUserAccounts?.forEach(account => {
                        generateMediaApiAccountCalls.push(this.usersApi.generateApiKey(account.accountId, newUserId));
                    });

                    if (generateMediaApiAccountCalls.length) {
                        return combineLatest(generateMediaApiAccountCalls).pipe(
                            catchError(err => of(newUserId)),
                            map(x => newUserId)
                        );
                    } else {
                        return of(newUserId);
                    }
                }))
                .subscribe({
                    next: (newUserId) => {
                        this.messageService.add(
                            {
                                severity : messageSeverityEnum.success,
                                summary : this.localizationService.transform(
                                    'AMT.createUser.growlMsg.success',
                                    {
                                        first: user.firstName,
                                        last: user.lastName
                                    }
                                )
                            });

                        if (addToCurrentAccount) {
                            this.messageService.add(
                                {
                                    severity : messageSeverityEnum.success,
                                    summary : this.localizationService.transform(
                                        'AMT.addToAccount.growlMsg.success',
                                        {
                                            first: user.firstName,
                                            last: user.lastName,
                                            account: this.selectedAccount.name
                                        }
                                    )
                                }
                            );
                            this.fetchUsersForAccount(this.selectedAccount.id);
                        }
                        if (this.creatingUserFromButton) {
                            this.router.navigate(
                                ['AccountManagement', 'user', newUserId]
                            );
                        }
                        resolve(newUserId);
                    },
                    error: err => {
                        if (err.error?.details[0]?.code === this.duplicateUsernameErrorCode) {
                            this.messageService.add(
                                {
                                    severity: messageSeverityEnum.error,
                                    summary: this.localizationService.transform(
                                        'AMT.createUser.growlMsg.error.duplicate',
                                        {
                                            username: user.username
                                        }
                                    )
                                }
                            );
                        } else {
                            this.messageService.add(
                                {
                                    severity : messageSeverityEnum.error,
                                    summary : this.localizationService.transform(
                                        'AMT.createUser.growlMsg.error',
                                        {
                                            first: user.firstName,
                                            last: user.lastName
                                        }
                                    )
                                }
                            );
                        }
                        reject(err);
                    }
                });
        });
    }

    saveUser(user: UserModel): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.usersApi.updateUser(user.toApiModel()).subscribe(done => {
                this.messageService.add(
                    {
                        severity : messageSeverityEnum.success,
                        summary : this.localizationService.transform(
                            'AMT.saveUser.growlMsg.success',
                            {
                                first: user.firstName,
                                last: user.lastName
                            }
                        )
                    });

                if (this.currentMode === Mode.account) {
                    this.fetchUsersForAccount(this.selectedAccount.id);
                }

                resolve();
            },
            err => {
                this.messageService.add(
                    {
                        severity : messageSeverityEnum.error,
                        summary : this.localizationService.transform(
                            'AMT.editUser.growlMsg.error',
                            {
                                first: user.firstName,
                                last: user.lastName
                            }
                        )
                    }
                );
                reject(err);
            });
        });
    }

    selectAccount(accountId: number): Promise<void> {
        // TODO: Refactor to use simpler async logic
        return new Promise((resolve) => {
            this.usersApi.getAccount(accountId).pipe(
                catchError((err: any, caught: Observable<AccountApiModel>) => {
                    this.messageService.add(
                        {
                            severity : messageSeverityEnum.error,
                            summary : 'AMT.growlMsg.accountError'
                        });
                    this.router.navigate([]);
                    throw err;
                }))
                .subscribe((fullAccount: AccountApiModel)  => {
                    this.selectedAccount = new AccountModel(fullAccount);
                    resolve();
                    this.originalAccount = JSON.parse(JSON.stringify(this.selectedAccount));
                    this.selectedAccountProvider.next(this.selectedAccount);
                    this.setOpenAiPermission();
                    this.setAgenticFeaturePermission();
                    this.setMediaMonitoringApisPermission();

                    this.getQuotaInfo(accountId);

                    this.targetingAccountPreferences = {
                        geography: {
                            include: [],
                            exclude: []
                        },
                        baselineDistributions: 25000,
                        additionalDistributions: 0,
                        usedDistributions: 0,
                        csvExportLimit: null,
                        hasInfluencerStreams: true
                    } as TargetingAccountPreferencesApiModel;
                    this.targetingAccountPreferences$.next(this.targetingAccountPreferences);
                    if (
                        this.originalAccount.permissions.includes(userPermissionsEnum.advancedTargeting) ||
                        this.originalAccount.permissions.includes(userPermissionsEnum.mediaDatabaseApi)
                    ) {
                        this.originalTargetingAccountPreferences = {...this.targetingAccountPreferences};

                        this.targetingPreferencesApi.getAccountPreferences(accountId).subscribe(prefs => {
                            this.targetingAccountPreferences = prefs;
                            this.originalTargetingAccountPreferences = JSON.parse(JSON.stringify(prefs));
                            this.targetingAccountPreferences$.next(this.targetingAccountPreferences);
                        });
                    }

                    if (this.configurationService.accountHasAccessToThirdPartyPitchingOnEnvironment(accountId)) {
                        this.emailAccountConnectorConfig = {
                            privateConnectorLimit: 0,
                            sharedConnectorLimit: 0
                        };
                        this.originalEmailAccountConnectorConfig = {...this.emailAccountConnectorConfig};
                        this.emailAccountConnectorConfigSubject.next(this.emailAccountConnectorConfig);
                        this.emailAccountConnectorApi.getEmailAccountConnectorConfigByAccountId(accountId)
                            .pipe(catchError(err => {
                                if (err.status === 404) {
                                    this.hasEmailConnectorConfigSetup = false;
                                    return of(null);
                                }
                            })).subscribe(config => {
                                if (config) {
                                    this.hasEmailConnectorConfigSetup = true;
                                    this.emailAccountConnectorConfig = config;
                                    this.originalEmailAccountConnectorConfig = JSON.parse(JSON.stringify(this.emailAccountConnectorConfig));
                                    this.emailAccountConnectorConfigSubject.next(this.emailAccountConnectorConfig);
                                }
                            });

                        this.emailAccountConnectorUsageSubject.next({
                            sharedUsage: 0,
                            privateUsage: 0
                        });
                        this.emailAccountConnectorApi.getByAccountId(accountId).subscribe(connectors => {
                            let sharedUsage = 0;
                            let privateUsage = 0;

                            const userIdToPrivateUsage: Map<number, number> = new Map();

                            connectors?.forEach(connector => {
                                if (connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED) {
                                    sharedUsage ++;
                                } else {
                                    if (userIdToPrivateUsage.has(connector.userId)) {
                                        userIdToPrivateUsage.set(connector.userId, userIdToPrivateUsage.get(connector.userId) + 1);
                                    } else {
                                        userIdToPrivateUsage.set(connector.userId, 1);
                                    }
                                }
                            });

                            userIdToPrivateUsage.forEach(value => {
                                if (value > privateUsage) {
                                    privateUsage = value;
                                }
                            });

                            this.emailAccountConnectorUsageSubject.next({
                                sharedUsage,
                                privateUsage
                            });
                        });
                    }
                });

            this.originalMonitoringConfigs = null;
            this.monitoringConfigs = new MonitoringConfigsModel();
            this.monitoringConfigs$.next(this.monitoringConfigs);

            this.briefingConfigs = new BriefingConfig();
            this.briefingConfigs$.next(this.briefingConfigs);

            this.originalAgentConfiguration = null;
            this.agentConfiguration = null;
            this.agentConfiguration$.next(this.agentConfiguration);

            this.agentsApiService.getAgentConfiguration(accountId).pipe(
                catchError(() => of(null))
            ).subscribe(configuration => {
                this.agentConfiguration = configuration;
                this.originalAgentConfiguration = configuration ? JSON.parse(JSON.stringify(configuration)) : null;
                if (!this.agentConfiguration) {
                    this.agentConfiguration = { accountId, monitorQuestionLimit: 0 };
                }

                this.agentConfiguration$.next(this.agentConfiguration);
            });

            this.mediaServiceApi.getAccountStatusById(accountId).subscribe(status => {
                this.contentLibraryStorageSettings = new StorageSettingsModel(status);
                this.originalContentLibraryStorage = status.contentStorageLimitBytes;
                this.contentLibraryStorageSettingsSubject.next(this.contentLibraryStorageSettings);
            });

            this.monitoringAccountApi.getConfigs(accountId).subscribe(configs => {
                if (configs) {
                    this.monitoringConfigs = new MonitoringConfigsModel(configs);
                    if (!this.monitoringConfigs.tvEyesOptions) {
                        this.monitoringConfigs.tvEyesOptions = {
                            geos: new IncludeExcludeModel<string>(),
                            isArchiveProvisioned: false,
                            isDownloadProvisioned: false
                        };
                    }
                    this.originalMonitoringConfigs = JSON.parse(JSON.stringify(this.monitoringConfigs));
                    this.monitoringConfigs$.next(this.monitoringConfigs);
                } else {
                    this.originalMonitoringConfigs = null;
                }
            });

            this.briefingAccountApi.getById(accountId).subscribe(configs => {
                if (configs) {
                    this.briefingConfigs = new BriefingConfig(configs);
                    this.originalBriefingConfigs = JSON.parse(JSON.stringify(this.briefingConfigs));
                    this.briefingConfigs$.next(this.briefingConfigs);
                } else {
                    this.originalBriefingConfigs = null;
                }
            });

            this.fetchUsersForAccount(accountId);
            this.fetchNotesForAccount(accountId);
            this.fetchApiKeysForAccount(accountId);
        });
    }

    getQuotaInfo(accountId: number): void {
        // media subscription quota
        this.originalMediaSubscriptionQuotaGroup = null;
        this.mediaSubscriptionQuotaGroup = null;
        this.mediaSubscriptionQuotaGroup$.next(null);
        this.mediaSubscriptionApiService.getMediaSubscriptionQuotaInfo(accountId).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 404) {
                    // 404s are expected if no quota group previously provisioned
                    return of(null);
                }
            }),
            filter(quota => !!quota),
        ).subscribe(quota => {
            this.originalMediaSubscriptionQuotaGroup = JSON.parse(JSON.stringify(quota));
            this.mediaSubscriptionQuotaGroup = JSON.parse(JSON.stringify(quota));
            if (this.originalAccount.permissions.includes(userPermissionsEnum.mediaSubscriptions)) {
                this.toggleMonitoringQuotaGroup(true, MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION);
            } else if (this.mediaSubscriptionQuotaGroup?.enabled) {
                // Some how the backend says our quota is enabled even though we don't have permissions for it
                this.mediaSubscriptionQuotaGroup.enabled = false;
            }
        });

        // social listening quota
        this.originalSocialListeningQuotaGroup = null;
        this.socialListeningQuotaGroup = null;
        this.socialListeningQuotaGroup$.next(null);
        this.mediaSubscriptionApiService.getSocialListeningQuotaInfo(accountId).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 404) {
                    // 404s are expected if no quota group previously provisioned
                    return of(null);
                }
            }),
            filter(quota => !!quota),
        ).subscribe(quota => {
            this.originalSocialListeningQuotaGroup = JSON.parse(JSON.stringify(quota));
            this.socialListeningQuotaGroup = JSON.parse(JSON.stringify(quota));
            if (this.originalAccount.permissions.includes(userPermissionsEnum.socialIntelligence)) {
                this.toggleMonitoringQuotaGroup(true, MonitoringQuotaTypesEnum.SOCIAL_LISTENING);
            } else if (this.socialListeningQuotaGroup?.enabled) {
                // Some how the backend says our quota is enabled even though we don't have permissions for it
                this.socialListeningQuotaGroup.enabled = false;
            }
        });

        // targeting quota
        this.targetingAccountQuotaInfo = new TargetingQuotaInfo();
        this.originalTargetingAccountQuotaInfo = this.targetingAccountQuotaInfo;
        this.targetingAccountQuotaInfo$.next(this.targetingAccountQuotaInfo);
        if (
            this.originalAccount.permissions.includes(userPermissionsEnum.advancedTargeting) ||
            this.originalAccount.permissions.includes(userPermissionsEnum.mediaDatabaseApi)
        ) {
            this.targetingPreferencesService.getQuotaInfoByAccountId(accountId).subscribe(quotaInfo => {
                this.targetingAccountQuotaInfo = quotaInfo;
                this.originalTargetingAccountQuotaInfo = JSON.parse(JSON.stringify(this.targetingAccountQuotaInfo));
                this.targetingAccountQuotaInfo$.next(this.targetingAccountQuotaInfo);
            });
        }

        this.loadAutomationAgentQuota(accountId);

        if (this.originalAccount.permissions.indexOf(userPermissionsEnum.newswireplus) !== -1) {
            this.wireApi.getQuotas(accountId).subscribe(quotas => {
                this.wireQuotas = quotas;
                this.wireQuotas.sort((x1, x2) => x1.region.localeCompare(x2.region));
                this.originalWireQuotas = JSON.parse(JSON.stringify(this.wireQuotas));
                this.wireQuotas$.next(this.wireQuotas);
            });
        } else {
            // set the originalWireQuotas to be the same as wireQuotas (which is empty array)
            this.originalWireQuotas = JSON.parse(JSON.stringify(this.wireQuotas));
        }
    }

    fetchUsersForAccount(accountId: number) {
        this.loading$.next(true);
        forkJoin({
            users: this.usersApi.getAllUsersFromAccount(accountId),
            primaryAccountUsers: this.usersApi.searchUsers(accountId.toString(), true),
            apiKeyManagers: this.usersApi.getAllUsersFromAccount(accountId, userPermissionsEnum.apiKeyManagement)
        }).subscribe(({users, primaryAccountUsers, apiKeyManagers}) => {
            this.hasApiKeyManagersSubject.next(apiKeyManagers.some(user => user.isActive));
            this.selectedAccountUsers = users
                .map(user => {
                    const userModel = new UserModel(user);
                    const match = primaryAccountUsers.find(item => item.id === userModel.id);
                    if (match) {
                        userModel.primaryAccountId = accountId;
                    }
                    return userModel;
                })
                .sort((x1, x2) => {
                    if (x1.isActive === x2.isActive) {
                        return x1.username.toLocaleLowerCase().localeCompare(x2.username.toLocaleLowerCase());
                    } else if (x2.isActive) {
                        return 1;
                    } else {
                        return -1;
                    }
                });

            this.selectedAccountUsersProvider.next(this.selectedAccountUsers);

            if (this.selectedUser) {
                // The api returned a new object so we need to replace the ref to maintain
                // our current selection.
                this.selectedUser = this.selectedAccountUsers.find(user => user.id === this.selectedUser.id);
                this.selectedUser$.next(this.selectedUser);
            }
            this.loading$.next(false);
        });
    }

    fetchNotesForAccount(accountId: number) {
        this.accountNoteApi.getAccountNotes(accountId).pipe(
            filter(result => !!result),
            map(notes => notes.map(note => new AccountNoteModel(note)))
        ).subscribe(
            accountNotes => this.selectedAccountNotesSubject.next(accountNotes)
        );
    }

    fetchApiKeysForAccount(accountId: number) {
        this.apiKeyService.getApiKeys(accountId).pipe(
            filter(keys => !!keys),
        ).subscribe(keys => this.selectedAccountApiKeysSubject.next(keys));
    }

    performAccountNoteAction(
        action: ResourceActionTypes,
        note: AccountNoteModel,
        accountId: number
    ) {
        let apiCall$: Observable<any>;
        switch (action) {
            case ResourceActionTypes.ADD_NOTE:
                apiCall$ = this.accountNoteApi.createAccountNote(accountId, note.toApiModel());
                break;
            case ResourceActionTypes.EDIT_NOTE:
                apiCall$ = this.accountNoteApi.updateAccountNote(accountId, note.id, note.toApiModel());
                break;
            case ResourceActionTypes.DELETE_NOTE:
                apiCall$ = this.accountNoteApi.deleteAccountNote(accountId, note.id);
                break;
        }

        apiCall$.pipe(
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(`Discover.userNotes.${action}SuccessMessage`)
                });
            }),
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(`Discover.userNotes.${action}ErrorMessage`)
                });
                return throwError(() => error);
            }),
            tap(() => this.fetchNotesForAccount(accountId))
        ).subscribe();
    }

    updateApiKeyStatusForAccount(apiKey: ApiKey): Observable<void> {
        return defer(() => {
            if (!this.selectedAccount) {
                throw new Error('No account selected');
            }
            return this.apiKeyService.updateApiKeyStatus(
                apiKey,
                this.selectedAccount.id
            );
        });
    }

    private isValid(): boolean {
        if (JSON.stringify(this.monitoringConfigs) !== JSON.stringify(this.originalMonitoringConfigs)) {
            if (!this.monitoringConfigs.isValid()) {
                // The only error right now is burrelles license without accounts
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary : 'AMT.growlMsg.burrellesError'
                });
                return false;
            }
        }

        if (this.contentLibraryStorageSettings?.contentStorageLimitBytes < this.contentLibraryStorageSettings?.contentUsedBytes) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform(
                    'AMT.accountSettings.storageSettings.error.summary', { usedGB: this.contentLibraryStorageSettings.contentUsedBytes }),
                detail: this.localizationService.transform('AMT.accountSettings.storageSettings.error.detail'),
            });
            return false;
        };
        return true;
    }

    saveUserChanges() {
        const apiCalls: Observable<void>[] = [];
        if (JSON.stringify(this.selectedUser.toApiModel()) !== JSON.stringify(this.originalUser.toApiModel())) {
            apiCalls.push(this.usersApi.updateUser(this.selectedUser.toApiModel()).pipe(
                mergeMap(res => {
                    // generating apiKey for users that have media db api accounts
                    const generateMediaApiAccountCalls: Observable<string>[] = [];
                    this.selectedUser.mediaDatabaseUserAccounts?.forEach(account => {

                        if (!this.originalUser.accountsIds?.includes(account.accountId)) {
                            generateMediaApiAccountCalls.push(this.usersApi.generateApiKey(account.accountId, this.selectedUser.id));
                        }
                    });

                    if (generateMediaApiAccountCalls.length) {
                        return combineLatest(generateMediaApiAccountCalls).pipe(
                            catchError(err => of(null)),
                            map(x => null)
                        );
                    } else {
                        return of(null);
                    }
                }),
                tap(done => this.selectUser(this.selectedUser.id))));
        }

        if (apiCalls.length > 0) {
            this.loading$.next(true);
            combineLatest(apiCalls).pipe(
                catchError((error: HttpErrorResponse) => {
                    const errorDetail = CommonApiErrorModel.getErrorDetail(error.error);
                    switch (errorDetail?.code) {
                        case CommonApiErrorEnum.duplicateName:
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'UMT.errors.duplicateName.header',
                                detail: this.localizationService.transform('UMT.errors.duplicateName.message')
                            });
                            break;
                        default:
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'UMT.errors.generic.header',
                                detail: this.localizationService.transform('UMT.errors.generic.message')
                            });
                    }
                    return throwError(() => new Error(errorDetail?.message ?? error.message));
                }),
                finalize(() => this.loading$.next(false))
            ).subscribe(done => {
                this.loading$.next(false);
                this.refreshMediaDBAccountSubject.next(true);
                this.messageService.add({
                    severity : messageSeverityEnum.success,
                    summary : 'AMT.growlMsg.savedSuccessfully'
                });
            });
        } else {
            this.messageService.add({
                severity : messageSeverityEnum.warning,
                summary : 'AMT.growlMsg.noChanges'
            });
        }
    }

    saveAccountChanges() {
        const apiCalls: Observable<any>[] = [];

        if (!this.isValid()) {
            return;
        }

        // enable media db account permission case, we need to refetch account after saving to get access token and client ID
        const refetchMediaDbAccountInfo = !this.originalAccount.permissions.includes(userPermissionsEnum.mediaDatabaseApi)
            && this.selectedAccount.permissions.includes(userPermissionsEnum.mediaDatabaseApi);

        const hasMonitoring = this.selectedAccountHasPermission(userPermissionsEnum.advancedMonitoring);
        const accountHasMonitoringConfig = (
            hasMonitoring
            || this.selectedAccountHasPermission(userPermissionsEnum.socialIntelligence)
        );

        if (accountHasMonitoringConfig) {

            // If the account doesn't have monitoring, it can have no permissions
            if (!hasMonitoring) {
                this.monitoringConfigs.removeAllMonitoringLicenses();
            }

            if (hasMonitoring && !this.monitoringConfigs.hasAnyLicenses()) {
                this.messageService.add({
                    severity: messageSeverityEnum.warning,
                    summary: 'AMT.growlMsg.missingLicenses'
                });
                return;
            }

            if (hasMonitoring && !this.monitoringConfigs.sublicensesAreValid()) {
                this.messageService.add({
                    severity: messageSeverityEnum.warning,
                    summary: 'AMT.growlMsg.missingSubLicenses'
                });
                return;
            }

            if (!this.monitoringConfigs.hasSubscriptionLicenses() && this.selectedAccountHasPermission(userPermissionsEnum.mediaSubscriptions)) {
                this.messageService.add({
                    severity: messageSeverityEnum.warning,
                    summary: 'AMT.growlMsg.missingSubscriptions'
                });
                return;
            }

            if (this.monitoringConfigs.hasSubscriptionLicenses() && !this.selectedAccountHasPermission(userPermissionsEnum.mediaSubscriptions)) {
                // It should have userPermissionsEnum.subscriptions on, if it doesn't, add it
                this.selectedAccount.permissions = [
                    ...this.selectedAccount.permissions,
                    userPermissionsEnum.mediaSubscriptions
                ];
            }

            if (this.monitoringConfigs.hasMentionInsights && !this.selectedAccountHasPermission(userPermissionsEnum.openAI)) {
                // mention insights is controlled by open AI permission, without open AI permission, hasMentionInsights would not be valid
                this.monitoringConfigs.hasMentionInsights = false;
            }

            if (this.monitoringConfigs.hasCoverageInsights && !this.selectedAccountHasPermission(userPermissionsEnum.openAI)) {
                // mention insights is controlled by open AI permission, without open AI permission, hasCoverageInsights would not be valid
                this.monitoringConfigs.hasCoverageInsights = false;
            }
        } else {
            // No AM or Social, ensure account does not have subscriptions permissions
            const index = this.selectedAccount.permissions.indexOf(userPermissionsEnum.mediaSubscriptions);
            if (index !== -1) {
                this.selectedAccount.permissions.splice(index, 1);
            }
        }

        if (this.selectedAccount.isMfaEnabled && this.selectedAccount.enabledMfaTypes?.length === 0) {
            this.messageService.add({
                severity: messageSeverityEnum.warning,
                summary: 'AMT.growlMsg.missingMfaMethod'
            });
            return;
        };

        // Remove duplicate serviceIds
        // @HACK backend should not return duplicate services
        this.selectedAccount.permissions = [...new Set(this.selectedAccount.permissions)];
        this.originalAccount.permissions = [...new Set(this.originalAccount.permissions)];

        if (JSON.stringify(this.selectedAccount) !== JSON.stringify(this.originalAccount)) {
            if (this.selectedAccount.name.trim() !== this.originalAccount.name.trim()) {
                const listItem = this.listItems.find(item => item.value = this.selectedAccount.id);
                if (listItem) {
                    listItem.label = this.selectedAccount.name;
                }
            }
            const apiModel = this.selectedAccount.toApiModel();
            apiCalls.push(
                this.usersApi.updateAccount(apiModel).pipe(
                    map(done => {
                        this.originalAccount = JSON.parse(JSON.stringify(this.selectedAccount));
                    })
                )
            );
        }

        const updateActions$ = {
            updateMonitoring: tap(),
            updateBriefing: tap(),
            updateMediaSubscriptionQuotaGroup: tap(),
            updateSocialListeningQuotaGroup: tap(),
            updateTargetingPreferences: tap(),
            updateTargetingAccountQuotaInfo: tap(),
            updateWireQuotas: tap(),
            updateEmailAccountConnectorConfig: tap(),
            updateContentLibraryStorageSettings: tap(),
            updateAgentConfiguration: tap()
        };

        if (
            JSON.stringify(this.monitoringConfigs) !== JSON.stringify(this.originalMonitoringConfigs) &&
            accountHasMonitoringConfig
        ) {
            updateActions$.updateMonitoring = tap(() => {
                this.monitoringConfigs$.next(this.monitoringConfigs);
                this.monitorConfigService.refreshMonitoringConfigModel();
            });

            if (this.originalMonitoringConfigs === null) {
                apiCalls.push(
                    this.monitoringAccountApi.createConfigs(
                        this.selectedAccount.id,
                        this.monitoringConfigs.toApiModel()
                    ).pipe(
                        map(done => {
                            this.originalMonitoringConfigs = JSON.parse(JSON.stringify(this.monitoringConfigs));
                        })
                    )
                );
            } else {
                apiCalls.push(
                    this.monitoringAccountApi.updateConfigs(
                        this.selectedAccount.id,
                        this.monitoringConfigs.toApiModel(),
                        this.locale
                    ).pipe(
                        map(done => {
                            this.originalMonitoringConfigs = JSON.parse(JSON.stringify(this.monitoringConfigs));
                        })
                    )
                );
            }
        }

        // Delete Monitoring Configs
        if (!accountHasMonitoringConfig && this.originalMonitoringConfigs !== null) {
            this.originalMonitoringConfigs = null;
            apiCalls.push(
                this.monitoringAccountApi.deleteConfigs(this.selectedAccount.id)
            );
            updateActions$.updateMonitoring = tap(() => {
                this.monitoringConfigs = new MonitoringConfigsModel();
                this.monitoringConfigs$.next(this.monitoringConfigs);
                this.monitorConfigService.refreshMonitoringConfigModel();
            });
        }

        // add/edit Breifing Configs
        if (
            JSON.stringify(this.briefingConfigs) !== JSON.stringify(this.originalBriefingConfigs) &&
            hasMonitoring
        ) {
            updateActions$.updateBriefing = tap(() => {
                this.briefingConfigs$.next(this.briefingConfigs);
                this.briefingConfigService.refreshBriefingConfigModel();
            });

            if (this.originalBriefingConfigs === null) {
                apiCalls.push(
                    this.briefingAccountApi.create(
                        this.selectedAccount.id,
                        this.briefingConfigs.toApiModel()
                    ).pipe(
                        map(done => {
                            this.originalBriefingConfigs = JSON.parse(JSON.stringify(this.briefingConfigs));
                        })
                    )
                );
            } else {
                apiCalls.push(
                    this.briefingAccountApi.update(
                        this.selectedAccount.id,
                        this.briefingConfigs.toApiModel(),
                        this.locale
                    ).pipe(
                        map(done => {
                            this.originalBriefingConfigs = JSON.parse(JSON.stringify(this.briefingConfigs));
                        })
                    )
                );
            }
        }

        // Delete Breifing Configs
        if (!hasMonitoring && this.originalBriefingConfigs !== null) {
            this.originalBriefingConfigs = null;
            apiCalls.push(
                this.briefingAccountApi.delete(this.selectedAccount.id)
            );
            updateActions$.updateBriefing = tap(() => {
                this.briefingConfigs = new BriefingConfig();
                this.briefingConfigs$.next(this.briefingConfigs);
                this.briefingConfigService.refreshBriefingConfigModel();
            });
        }

        this.saveQuotaChanges(updateActions$, apiCalls, MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION);
        this.saveQuotaChanges(updateActions$, apiCalls, MonitoringQuotaTypesEnum.SOCIAL_LISTENING);

        if(this.contentLibraryStorageSettings?.contentStorageLimitBytes !== this.originalContentLibraryStorage) {
            updateActions$.updateContentLibraryStorageSettings = tap(() => {
                this.contentLibraryStorageSettingsSubject.next(this.contentLibraryStorageSettings);
            });

            apiCalls.push(
                // eslint-disable-next-line max-len
                this.mediaServiceApi.updateStorageLimit(this.selectedAccount.id, this.contentLibraryStorageSettings.toApiModelSettings()).pipe(
                    tap(id => {
                        this.originalContentLibraryStorage = JSON.parse(JSON.stringify(this.contentLibraryStorageSettings));
                    })
                )
            );
        }

        if (
            this.selectedAccountHasPermission(userPermissionsEnum.visibilityIntelligence)
            && JSON.stringify(this.agentConfiguration) !== JSON.stringify(this.originalAgentConfiguration)
        ) {
            updateActions$.updateAgentConfiguration = tap(() => {
                this.agentConfiguration$.next(this.agentConfiguration);
            });

            if (this.originalAgentConfiguration === null) {
                apiCalls.push(
                    this.agentsApiService.createAgentConfiguration({
                        accountId: this.selectedAccount.id,
                        monitorQuestionLimit: this.agentConfiguration?.monitorQuestionLimit ?? 0
                    }).pipe(
                        map(configuration => {
                            this.agentConfiguration = configuration;
                            this.originalAgentConfiguration = JSON.parse(JSON.stringify(configuration));
                        })
                    )
                );
            } else {
                apiCalls.push(
                    this.agentsApiService.updateAgentConfiguration(this.selectedAccount.id, {
                        monitorQuestionLimit: this.agentConfiguration?.monitorQuestionLimit
                    }).pipe(
                        map(configuration => {
                            this.agentConfiguration = configuration;
                            this.originalAgentConfiguration = JSON.parse(JSON.stringify(configuration));
                        })
                    )
                );
            }
        }

        if (JSON.stringify(this.targetingAccountPreferences) !== JSON.stringify(this.originalTargetingAccountPreferences)) {
            updateActions$.updateTargetingPreferences = tap(() => {
                this.targetingAccountPreferences$.next(this.targetingAccountPreferences);
                this.targetingPreferencesService.reloadPreferences();
            });

            apiCalls.push(
                this.targetingPreferencesApi.updateAccountPreferences(this.selectedAccount.id, this.targetingAccountPreferences).pipe(
                    map(id => {
                        this.originalTargetingAccountPreferences = JSON.parse(JSON.stringify(this.targetingAccountPreferences));
                    }),
                )
            );
        }

        if (JSON.stringify(this.emailAccountConnectorConfig) !== JSON.stringify(this.originalEmailAccountConnectorConfig)) {
            updateActions$.updateEmailAccountConnectorConfig = tap(() => {
                this.emailAccountConnectorConfigSubject.next(this.emailAccountConnectorConfig);
            });

            if (this.hasEmailConnectorConfigSetup) {
                apiCalls.push(
                    this.emailAccountConnectorApi.updateEmailAccountConnectorConfig(this.selectedAccount.id, this.emailAccountConnectorConfig).pipe(
                        map(id => {
                            this.originalEmailAccountConnectorConfig = JSON.parse(JSON.stringify(this.emailAccountConnectorConfig));
                        }),
                    )
                );
            } else {
                apiCalls.push(
                    this.emailAccountConnectorApi.createEmailAccountConnectorConfig(this.selectedAccount.id, this.emailAccountConnectorConfig).pipe(
                        map(id => {
                            this.originalEmailAccountConnectorConfig = JSON.parse(JSON.stringify(this.emailAccountConnectorConfig));
                        }),
                    )
                );
            }
        }

        // We don't want to compare the nextResetDate as it is calculated in the component and sometimes not set in the b/e directly
        if (this.targetingAccountQuotaInfo?.customResetDate !== this.originalTargetingAccountQuotaInfo?.customResetDate
            || this.targetingAccountQuotaInfo?.periodStartDate !== this.originalTargetingAccountQuotaInfo?.periodStartDate) {
            updateActions$.updateTargetingAccountQuotaInfo = tap(() => this.targetingAccountQuotaInfo$.next(this.targetingAccountQuotaInfo));

            apiCalls.push(
                this.targetingPreferencesService.setAccountQuotaResetDate(this.selectedAccount.id, this.targetingAccountQuotaInfo.customResetDate).pipe(
                    map(id => {
                        this.originalTargetingAccountQuotaInfo = JSON.parse(JSON.stringify(this.targetingAccountQuotaInfo));
                    }),
                )
            );
        }


        // An account that has just been given the feature has no pool yet, and nothing to diff
        // against - it is provisioned on this save, with the default limit the model starts at
        if (
            this.selectedAccountHasPermission(userPermissionsEnum.agenticFeature)
            && (
                !this.automationAgentQuota.provisioned
                || JSON.stringify(this.automationAgentQuota) !== JSON.stringify(this.originalAutomationAgentQuota)
            )
        ) {
            const quotaModel = this.automationAgentQuota;
            const additionalChanged = quotaModel.additionalQuota !== this.originalAutomationAgentQuota?.additionalQuota;

            apiCalls.push(
                this.aiAgentQuotaAdminApi.upsert(this.selectedAccount.id, quotaModel.toApiModel()).pipe(
                    // The top up is a separate endpoint - it applies to this billing period only
                    switchMap(quota => additionalChanged
                        ? this.aiAgentQuotaAdminApi.setAdditionalQuota(
                            this.selectedAccount.id,
                            quotaModel.toAdditionalQuotaRequest()
                        )
                        : of(quota)),
                    map(quota => {
                        // Take the pool back from the response - the backend owns count and the window
                        this.automationAgentQuota = new AutomationAgentQuotaModel(quota);
                        this.originalAutomationAgentQuota = JSON.parse(JSON.stringify(this.automationAgentQuota));
                        // Pushed here rather than through updateActions$, which only runs when the
                        // admin is editing their own account - the panel would otherwise hold the
                        // pre-save pool and go on saying nothing has been provisioned
                        this.automationAgentQuota$.next(this.automationAgentQuota);
                    })
                )
            );
        }

        if (JSON.stringify(this.wireQuotas) !== JSON.stringify(this.originalWireQuotas)) {
            updateActions$.updateWireQuotas = tap(() => this.wireQuotas$.next(this.wireQuotas));

            apiCalls.push(
                this.wireApi.saveQuotas(this.selectedAccount.id, this.wireQuotas).pipe(
                    map(done => {
                        this.originalWireQuotas = JSON.parse(JSON.stringify(this.wireQuotas));
                    }),
                )
            );
        }

        if (apiCalls.length > 0) {
            this.loading$.next(true);
            forkJoin(apiCalls).pipe(
                switchMap(() => this.currentUserStateService.getAccount().pipe(
                    take(1)
                )),
                switchMap((account) => {
                    if (account.id === this.selectedAccount.id) {
                        return this.currentUserStateService.loadUserState$().pipe(
                            updateActions$.updateMonitoring,
                            updateActions$.updateMediaSubscriptionQuotaGroup,
                            updateActions$.updateSocialListeningQuotaGroup,
                            updateActions$.updateTargetingAccountQuotaInfo,
                            updateActions$.updateTargetingPreferences,
                            updateActions$.updateWireQuotas,
                            updateActions$.updateEmailAccountConnectorConfig,
                            updateActions$.updateContentLibraryStorageSettings,
                            updateActions$.updateAgentConfiguration,
                        );
                    }
                    return of(null);
                }),
                switchMap(() => {
                    if (refetchMediaDbAccountInfo) {
                        return this.getAccountById(this.selectedAccount.id).pipe(tap(x => {
                            this.selectedAccount = x;
                            this.originalAccount = JSON.parse(JSON.stringify(this.selectedAccount));
                            this.selectedAccountProvider.next(this.selectedAccount);
                        }));
                    }
                    return of(null);
                }),
                catchError((error) => {
                    const errorMessages: string[] = error?.error?.details?.map(err => err.message)
                        || (error?.error?.message ? [error.error.message] : [])
                        || [];
                    if(errorMessages.length > 0) {
                        errorMessages.forEach(message => {
                            if (message.includes('Prominence group limit too low')) {
                                // example message: Prominence group limit too low. Number of groups currently in use: 3
                                // we want to extract the number in use and show it for users
                                this.prominenceInUse = +message.match(/\d/g);
                                if (this.monitoringConfigs) {
                                    this.monitoringConfigs.prominenceGroupLimit = this.originalMonitoringConfigs?.prominenceGroupLimit ?? 0;
                                }
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'AMT.errors.hasProminenceLimitError.growl.summary',
                                    detail: this.localizationService
                                        .transform('AMT.errors.hasProminenceLimitError', { count: this.prominenceInUse })
                                });
                            } else if (message.includes('The account cannot have Agility SSO and OAuth SSO enabled at the same time')) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'AMT.errors.sso.growl.summary',
                                    detail: this.localizationService.transform('AMT.errors.sso.message')
                                });
                            } else if (message.includes('Current number of tagging topics')) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'AMT.errors.topicsThatTag.growl.summary',
                                    detail: message
                                });
                            } else if (message.includes('questions currently active')) {
                                if (this.agentConfiguration) {
                                    this.agentConfiguration.monitorQuestionLimit = this.originalAgentConfiguration?.monitorQuestionLimit ?? 0;
                                    this.agentConfiguration$.next(this.agentConfiguration);
                                }
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'AMT.errors.hasMonitorQuestionLimitError.growl.summary',
                                    detail: message
                                });
                            }
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'AMT.errors.update.defaultMessage'
                        });
                    }

                    return throwError(error);
                }),
                finalize(() => this.loading$.next(false)))
                .subscribe(done => {
                    this.loading$.next(false);
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary : 'AMT.growlMsg.savedSuccessfully'
                    });
                });
        } else {
            // TODO : Disable/hide the button instead
            this.messageService.add({
                severity : messageSeverityEnum.warning,
                summary : 'AMT.growlMsg.noChanges'
            });
        }
    }

    /*/ / Note : not called yet.
    savePermissions() {
        const permissionsToUpdate = this.selectedUserPermissions.find(x => x.accountId === this.selectedAccount.id).permissionIds;

        // TODO : generalized dirty-checking that also disables the save button
        let dirty = false;

        for (const permission of this.permissions) {
            // Newly added by UI
            if (permission.hasPermission && !permissionsToUpdate.find(x => x === permission.permissionId)) {
                permissionsToUpdate.push(permission.permissionId);
                dirty = true;
            // Newly removed by UI
            } else if (!permission.hasPermission) {
                const index = permissionsToUpdate.indexOf(permission.permissionId);
                if (index > -1) {
                    permissionsToUpdate.splice(index, 1);
                    dirty = true;
                }
            }
        }
        if (dirty) {
            this.usersApi.updatePermissions(
                this.selectedAccount.id,
                this.selectedUser.id,
                permissionsToUpdate
            ).subscribe(done => {
                this.messageService.add({
                    severity : messageSeverityEnum.success,
                    summary : 'Saved successfully'
                });

                // Refresh data from server
                this.selectUser(this.selectedUser);
            });
        }
    }*/

    toggleMonitoringQuotaGroup(enable: boolean, type: MonitoringQuotaTypesEnum): void {
        const originalQuotaGroup = type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION ?
            this.originalMediaSubscriptionQuotaGroup : this.originalSocialListeningQuotaGroup;
        let quotaGroup = type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION ? this.mediaSubscriptionQuotaGroup : this.socialListeningQuotaGroup;
        const quotaGroup$ = type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION ? this.mediaSubscriptionQuotaGroup$ : this.socialListeningQuotaGroup$;
        if (enable) {
            // Acts as an "undo"
            if (originalQuotaGroup) {
                quotaGroup = JSON.parse(JSON.stringify(originalQuotaGroup));
                quotaGroup.enabled = true;
            } else {
                quotaGroup= {
                    additionalCount: 0,
                    currentCount: 0,
                    maxCount: 25000, // Most frequent value in the DB :)
                    accountId: this.selectedAccount.id,
                    enabled: true,
                    quotaType: type
                };
            }
            if (type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION) {
                this.mediaSubscriptionQuotaGroup = quotaGroup;
            } else {
                this.socialListeningQuotaGroup = quotaGroup;
            }
            quotaGroup$.next(quotaGroup);
        } else if (quotaGroup) {
            quotaGroup.enabled = false;
            quotaGroup$.next(quotaGroup);
        }
    }

    saveQuotaChanges(updateActions$: any, apiCalls: Observable<any>[], type: MonitoringQuotaTypesEnum): void {
        const isMediaSubscription = type === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION;
        const updateFunction = isMediaSubscription ? 'updateMediaSubscriptionQuotaGroup' : 'updateSocialListeningQuotaGroup';
        const originalQuotaGroup = isMediaSubscription ? this.originalMediaSubscriptionQuotaGroup : this.originalSocialListeningQuotaGroup;
        const quotaGroup = isMediaSubscription ? this.mediaSubscriptionQuotaGroup : this.socialListeningQuotaGroup;
        const quotaGroup$ = isMediaSubscription ? this.mediaSubscriptionQuotaGroup$ : this.socialListeningQuotaGroup$;

        if (JSON.stringify(quotaGroup) !== JSON.stringify(originalQuotaGroup)) {
            updateActions$[updateFunction] = tap(() => {
                quotaGroup$.next(quotaGroup);
            });

            if (quotaGroup.id) {
                apiCalls.push(
                    this.mediaSubscriptionApiService.updateQuotaInfo(quotaGroup).pipe(
                        map(done => {
                            if (isMediaSubscription) {
                                this.originalMediaSubscriptionQuotaGroup = JSON.parse(JSON.stringify(quotaGroup));
                            } else {
                                this.originalSocialListeningQuotaGroup = JSON.parse(JSON.stringify(quotaGroup));
                            }
                        })
                    )
                );
            } else {
                apiCalls.push(
                    this.mediaSubscriptionApiService.createQuotaInfo(quotaGroup).pipe(
                        map(id => {
                            quotaGroup.id = id;
                            if (isMediaSubscription) {
                                this.originalMediaSubscriptionQuotaGroup = JSON.parse(JSON.stringify(quotaGroup));
                            } else {
                                this.originalSocialListeningQuotaGroup = JSON.parse(JSON.stringify(quotaGroup));
                            }
                        })
                    )
                );
            }
        }
    }

    getSelectedUser(): Observable<UserModel> {
        return this.selectedUser$;
    }

    getSelectedAccount(): Observable<AccountModel> {
        return this.selectedAccountProvider;
    }

    getSelectedUserAccounts(): Observable<AccountModel[]> {
        return this.selectedUserAccountsProvider;
    }

    setSelectedUserPrimaryAccount(primaryAccount: AccountSummaryModel): void {
        this.primaryAccount = primaryAccount;
        this.primaryAccount$.next(this.primaryAccount);
    }

    getSelectedUserPrimaryAccount(): Observable<AccountSummaryModel> {
        return this.primaryAccount$;
    }

    getSelectedAccountUsers(): Observable<UserModel[]> {
        return this.selectedAccountUsersProvider;
    }

    getSelectedUserPermissions(): Observable<UserPermissionModel[]> {
        return this.permissionsProvider;
    }

    getMediaSubscriptionQuotaGroup(): Observable<QuotaInformation> {
        return this.mediaSubscriptionQuotaGroup$;
    }

    getSocialListeningQuotaGroup(): Observable<QuotaInformation> {
        return this.socialListeningQuotaGroup$;
    }

    getWireQuotas(): Observable<WireQuotaApiModel[]> {
        return this.wireQuotas$;
    }

    getAutomationAgentQuota(): Observable<AutomationAgentQuotaModel> {
        return this.automationAgentQuota$.asObservable();
    }

    /**
     * An account that has never had a pool provisioned 404s, which is not an error here - the admin
     * is about to give it one, and the first save will create it.
     */
    private loadAutomationAgentQuota(accountId: number): void {
        this.automationAgentQuota = new AutomationAgentQuotaModel();
        this.originalAutomationAgentQuota = JSON.parse(JSON.stringify(this.automationAgentQuota));
        this.automationAgentQuota$.next(this.automationAgentQuota);

        if (!this.originalAccount.permissions.includes(userPermissionsEnum.agenticFeature)) {
            return;
        }

        this.aiAgentQuotaAdminApi.get(accountId).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 404) {
                    return of(null);
                }

                throw err;
            }),
            filter(quota => !!quota)
        ).subscribe(quota => {
            this.automationAgentQuota = new AutomationAgentQuotaModel(quota);
            this.originalAutomationAgentQuota = JSON.parse(JSON.stringify(this.automationAgentQuota));
            this.automationAgentQuota$.next(this.automationAgentQuota);
        });
    }

    getMonitoringConfig(): Observable<MonitoringConfigsModel> {
        return this.monitoringConfigs$.asObservable();
    }

    getAgentConfiguration(): Observable<AgentConfigurationApi | null> {
        return this.agentConfiguration$.asObservable();
    }

    setMonitorQuestionLimit(monitorQuestionLimit: number): void {
        this.agentConfiguration = { accountId: this.selectedAccount?.id, monitorQuestionLimit };
        this.agentConfiguration$.next(this.agentConfiguration);
    }

    getLicenses(): Observable<SubscriptionLicenses> {
        return this.monitoringConfigs$.pipe(map(x => x.licenses));
    }

    getSublicenses(): Observable<SubscriptionLicenses> {
        return this.monitoringConfigs$.pipe(map(x => x.sublicenses));
    }
    getSocialLicenses(): Observable<SubscriptionLicenses> {
        return this.monitoringConfigs$.pipe(map(x => x.socialLicenses));
    }

    getTvEyesOptions(): Observable<MonitoringConfigTVEyes> {
        return this.monitoringConfigs$.pipe(map(x => x.tvEyesOptions));
    }

    getGeoRestrictions(): Observable<GeoRestriction[]> {
        return this.monitoringConfigs$.pipe(map(x => x.geoRestrictions));
    }

    getBurrellesAccountIds(): Observable<string[]> {
        return this.monitoringConfigs$.pipe(map(x => x.burrellesAccountIds));
    }

    getTargetingAccountPreferences(): Observable<TargetingAccountPreferencesApiModel> {
        return this.targetingAccountPreferences$;
    }
    getTargetingAccountQuotaInfo(): Observable<TargetingQuotaInfo> {
        return this.targetingAccountQuotaInfo$;
    }

    isAccountExpired(account: AccountModel) {
        return account && account.expiryDate <= moment().utc();
    }

    getBriefingConfig(): Observable<BriefingConfig> {
        return this.briefingConfigs$;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    getReadOnly(): Observable<boolean> {
        const userStateProvider: Observable<UserStateModel> = this.currentUserStateService.getState();
        const validStateProvider: Observable<UserStateModel> = userStateProvider.pipe(filter(x => x instanceof UserStateModel));
        const readOnlyProvider = validStateProvider.pipe(map(this.isReadOnly));

        return readOnlyProvider;
    }

    private isReadOnly(currentUserState: UserStateModel): boolean {
        const hasWritePermission: boolean = currentUserState.hasPermission(userPermissionsEnum.accountManager);
        const hasReadOnlyPermission: boolean = currentUserState.hasPermission(userPermissionsEnum.accountsReadOnlyManager);
        const isReadOnly = hasReadOnlyPermission && !hasWritePermission;

        return isReadOnly;
    }


    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItems$;
    }
    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItem$;
    }
    selectDropdownItem(item: DropdownOptionModel): void{
        this.currentMode = item.value;
        this.clearFilters();
        this.applyConfig();
    }

    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getListIsComplete(): Observable<boolean>{
        return of(true);
    }

    getMoreListItems(): void {

    }

    getSelectedListItem(): Observable<LeftSidebarListItem | null> {
        return this.selectedListItem$;
    }

    selectListItem(item: LeftSidebarListItem): void {
        switch (this.currentMode) {
            case Mode.account :
                this.router.navigate(
                    ['AccountManagement', 'account', item.value]
                );
                break;
            case Mode.user :
                this.router.navigate(
                    ['AccountManagement', 'user', item.value]
                );
                break;
        }
    }

    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton): void {

    }

    getReorderEnabled(): Observable<boolean>{
        return of(false);
    }

    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean){

    }

    isAmplifyButtonEnabled(): Observable<boolean>{
        return this.getReadOnly().pipe(map(readonly => !readonly && this.amtLeftSidebarModeConfigMap.get(this.currentMode).isAmplifyButtonEnabled));
    }

    amplifyButtonClicked(): void {
        this.amtLeftSidebarModeConfigMap.get(this.currentMode).amplifyAction();
    }

    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizations$;
    }

    applyConfig() {
        const config = this.amtLeftSidebarModeConfigMap.get(this.currentMode);

        if (config) {
            const listItems: LeftSidebarListItem[] = [...config.listItemSource];

            this.listItems = listItems;
            this.listItemsProvider.next(listItems);

            this.localizations = config.localizations;
            this.localizations$.next(this.localizations);

            // this.listIsComplete = config.isComplete;
            // this.listIsCompleteProvider.next(this.listIsComplete);
            // this.buttonEnabled = config.isAmplifyButtonEnabled;
            // this.buttonEnabledProvider.next(this.buttonEnabled);

            this.setFilters(config.filters);
            config.prepareFilters();
            this.applyFilters();
        }
    }

    // Override since backend will do the filtering
    applyFilters(): void {
        super.applyFilters();
        if (this.nameFilter.getValue()?.length > 0) {
            const nameValue = this.nameFilter.getValue()[0]?.trim();
            switch (this.currentMode) {
                case Mode.account :
                    this.usersApi.searchAccounts(nameValue).subscribe(items => {
                        this.listItems = items.map(account => {
                            return {
                                label : account.name,
                                value : account.id,
                                status : account.id,
                                buttons : null
                            };
                        });
                        this.groupListItems();
                        this.listItemsProvider.next(this.listItems);
                    });
                    break;
                case Mode.user : {
                    this.usersApi.searchUsers(nameValue).subscribe(items => {
                        this.listItems = items.map(user => {
                            return {
                                label : user.firstName + ' ' + user.lastName,
                                value : user.id,
                                status : user.id,
                                buttons : null
                            };
                        });
                        this.groupListItems();
                        this.listItemsProvider.next(this.listItems);
                    });
                }
            }
        } else {
            // Filters are cleared, clear list
            this.listItemsProvider.next([]);
        }
    }

    getQuickFilterMinLength() {
        return 1;
    }

    setBurrellesAccountIds(ids: string[]): void {
        this.monitoringConfigs.burrellesAccountIds = ids;
    }

    setTvEyesOptions(tvEyesOptions: MonitoringConfigTVEyes) {
        this.monitoringConfigs.tvEyesOptions = tvEyesOptions;
    }

    setGeoRestrictions(geoRestrictions: GeoRestriction[]) {
        this.monitoringConfigs.geoRestrictions = geoRestrictions;
    }

    /**
     * HACK: Necessary as MonitoringConfig and licenses are handled by nested object reference,
     * so changes are propagated, but the event of the change is not
     */
    notifyLicensesChanged() {
        this.licensesChangedSubject$.next();
    }

    accountHasSubscriptionPermissions(): boolean {
        return this.selectedAccountHasPermission(userPermissionsEnum.mediaSubscriptions);
    }

    setOpenAiPermission(): void {
        this.hasOpenAiPermissionSubject.next(this.selectedAccountHasPermission(userPermissionsEnum.openAI));
    }

    setAgenticFeaturePermission(): void {
        this.hasAgenticFeaturePermissionSubject.next(this.selectedAccountHasPermission(userPermissionsEnum.agenticFeature));
    }

    setMediaMonitoringApisPermission(): void {
        this.hasMediaMonitoringApisSubject.next(this.selectedAccountHasPermission(userPermissionsEnum.mediaMonitoringApi));
    }

    toggleMediaSubscriptionPermission(enable: boolean) {
        if (!this.selectedAccount) {
            return;
        }

        if (enable && !this.selectedAccountHasPermission(userPermissionsEnum.mediaSubscriptions)) {
            this.selectedAccount.permissions.push(userPermissionsEnum.mediaSubscriptions);
            return;
        }

        if (!enable && this.selectedAccountHasPermission(userPermissionsEnum.mediaSubscriptions)) {
            this.selectedAccount.permissions.splice(this.selectedAccount.permissions.indexOf(userPermissionsEnum.mediaSubscriptions), 1);
        }
    }

    private selectedAccountHasPermission(permission: userPermissionsEnum): boolean {
        return this.selectedAccount.permissions.includes(permission);
    }

    handleGroupDelete(ids: number[]) {
        // Not used but required in LeftSidebarService
    }

    moveListItem(item: LeftSidebarListItem) {
        // Not used but required in LeftSidebarService
    }

    getAccountById(id: number): Observable<AccountModel> {
        return this.usersApi.getAccount(id).pipe(map(x => new AccountModel(x)));
    }

    getUserApiKey(accountId: number, userId: number): Observable<string> {
        if (!userId) {
            return of(null);
        }
        return this.usersApi.getUserApiKey(accountId, userId);
    }

    getUserAccountPreferences(accountId: number, userId: number): Observable<UpdatePreferencesApiModel> {
        if (!userId) {
            return of(null);
        }

        return this.usersApi.getUserAccountPreferences(accountId, userId);
    }

    updateUserAccountPreferences(accountId: number, userId: number, preference: UpdatePreferencesApiModel): Observable<null> {
        return this.usersApi.updateUserAccountPreferences(accountId, userId, preference);
    }

    refMediaSublicenseChange() {
        this.monitoringConfigs.licenses[LicensesEnum.REFMEDIA] = this.monitoringConfigs.sublicenses[SublicensesEnum.REFMEDIA_ONLINE_PRINT] ||
                                                                    this.monitoringConfigs.sublicenses[SublicensesEnum.REFMEDIA_PARLIAMENTARY] ||
                                                                    this.monitoringConfigs.sublicenses[SublicensesEnum.REFMEDIA_BROADCAST];
    }
}
